import {
    useEffect,
    useState,
} from 'react'

/**
 * Better useState — takes a URL param as the default value of state.
 * State can be changed like classic useState.
 *
 * USAGE:  const [foo, setFoo] = useSearchState(null)
 *         => https://bar.baz/bak?foo=xyz  ->  foo === null
 *
 * USAGE:  const [foo, setFoo] = useSearchState(null, 'foo')
 *         => https://bar.baz/bak?foo=xyz  ->  foo === "xyz"
 *
 * USAGE:  const [foo, setFoo] = useSearchState(null, 'foo')
 *         => https://bar.baz/bak          ->  foo === null
 *
 * USAGE:  const [foo, setFoo] = useSearchState(null, 'foo', true)
 *         => https://bar.baz/bak?foo=eyJpZCI6Inh5eiJ9  ->  foo === { id: "xyz" }
 *
 * @param initialState - initial state value
 * @param searchQueryParameter - URL search parameter name to read on mount
 * @param encodeParameters - when true the URL param must be a base64-encoded JSON string
 * @returns Tuple of [state, setState] matching the useState signature
 */
function useSearchState<T>(
    initialState: T,
    searchQueryParameter: string | null = null,
    encodeParameters = true,
): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [items, setItems] = useState<T>(initialState)

    const searchParams = new URLSearchParams(window.location.search)

    useEffect(() => {
        if (searchQueryParameter && searchParams.get(searchQueryParameter)) {
            const searchParameter = searchParams.get(searchQueryParameter) as string

            if (encodeParameters) {
                setItems(JSON.parse(atob(searchParameter)) as T)
            } else {
                setItems(searchParameter as unknown as T)
            }
        }
        // Run only on mount — searchParams is derived from window.location at call time,
        // and encodeParameters/searchQueryParameter are mount-time arguments that do not change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return [
        items,
        setItems,
    ]
}

export default useSearchState
